import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import apiV2 from "api-v2";
import { useCurrentCompanyId } from "hooks/auth";
import { StaleTime } from "newui/store/tanstack-base";
import { unwrapApiV2 } from "../utils/unwrapApiV2";
import type { RfxSortValue } from "../config/rfx-sort-options";
import type {
	RfxListItemResource,
	RfxListScope,
	RfxListTab,
} from "../types/rfx-list.type";

export interface RfxListPageMeta {
	current_page: number;
	last_page: number;
	per_page: number;
	total: number;
}

export interface RfxListTabPayload<T = RfxListItemResource> {
	data: T[];
	meta: RfxListPageMeta;
}

/**
 * `GET /api/v2/rfq?scope=&tab=` response — tab-keyed at the top level
 * (`{ data: { <tab>: { data, meta } }, filters }`, unwrapped to the inner
 * `data` map by `unwrapApiV2`). Per-row field consumption is 008/009/010's
 * concern; this hook only exposes the requested tab's slice.
 */
export type RfxListIndexResponse<
	T = RfxListItemResource,
	TTab extends string = RfxListTab,
> = Partial<Record<TTab, RfxListTabPayload<T>>>;

/**
 * `search`/`sort`/`filters` are additive (TASK-017) — `RfxClientViewService::
 * applyFilterSortSearch()` already reads them straight off the raw request,
 * so no backend change is needed. Omitting them keeps the outgoing request
 * byte-identical to the pre-017 `{page}`-only shape (getRfxList spreads
 * `filters` directly into `params`, so an absent key is never sent).
 */
export interface RfxListQueryFilters {
	filters?: Record<string, unknown>;
	page?: number;
	search?: string;
	/**
	 * All four legacy sort values (see `config/rfx-sort-options.ts`). Widened
	 * from `"asc" | "desc"`: PRO-637 narrowed this to a binary toggle, but the
	 * API has always resolved `CREATED`/`LAST_ACTIVITY` too via
	 * `IsSortableModel::$sorting`, so the extra values need no backend change.
	 */
	sort?: RfxSortValue;
}

/**
 * Filter options the list endpoint returns alongside the rows
 * (`RFxController::index` → `'filters' => $scoped['filters']`). Drives the
 * filter drawer's available options.
 *
 * NOTE: the legacy drawer read these from the redux `rfq` slice, which the
 * react-query list pages never populate — hence the drawer is fed from this
 * envelope instead. See `RfxFilterDrawer`.
 */
export interface RfxListFilterOptions {
	sectors?: { id: number | string; name: string }[];
	states?: string[];
	statuses?: { id: number | string; name: string }[];
}

/**
 * Row total per tab for EVERY tab in the scope (`RFxController::index` →
 * `'counts' => $rfxViewService->getScopedCounts()`). The endpoint only returns
 * rows for the requested tab, so `meta.total` covers that tab alone — these are
 * the only counts available for the tabs the user has not opened, and therefore
 * the sole source for the tab-strip badges.
 *
 * Deliberately UNFILTERED server-side: they must not move as the user searches.
 */
export type RfxListTabCounts<TTab extends string = RfxListTab> = Partial<
	Record<TTab, number>
>;

interface RfxListEnvelope<T, TTab extends string> {
	counts?: RfxListTabCounts<TTab>;
	data: RfxListIndexResponse<T, TTab>;
	filters?: RfxListFilterOptions;
}

interface RfxListResult<T, TTab extends string> {
	counts: RfxListTabCounts<TTab>;
	filterOptions: RfxListFilterOptions;
	tabs: RfxListIndexResponse<T, TTab>;
}

const getRfxList = async <T = RfxListItemResource, TTab extends string = RfxListTab>(
	scope: RfxListScope,
	tab: TTab,
	filters: RfxListQueryFilters
): Promise<RfxListResult<T, TTab>> => {
	const { data } = await apiV2.get("rfq", {
		params: { scope, tab, ...filters },
	});

	// `filters` is a SIBLING of `data` in the envelope, so it must be read
	// before unwrapping — `unwrapApiV2` returns only the inner `data` map.
	const envelope = (data ?? {}) as RfxListEnvelope<T, TTab>;

	return {
		tabs: unwrapApiV2<RfxListIndexResponse<T, TTab>>(data),
		filterOptions: envelope.filters ?? {},
		counts: envelope.counts ?? {},
	};
};

export const rfxListQueryKey = (
	companyId: number | null | undefined,
	scope: RfxListScope,
	tab: string,
	filters: RfxListQueryFilters
) => ["rfx-list", companyId ?? null, scope, tab, filters] as const;

export const useRfxList = <T = RfxListItemResource, TTab extends string = RfxListTab>(
	scope: RfxListScope,
	tab: TTab,
	filters: RfxListQueryFilters = {}
) => {
	const companyId = useCurrentCompanyId();

	const query = useQuery<RfxListResult<T, TTab>>({
		queryKey: rfxListQueryKey(companyId, scope, tab, filters),
		queryFn: () => getRfxList<T, TTab>(scope, tab, filters),
		enabled: companyId !== null && companyId !== undefined,
		staleTime: StaleTime.Short,
		// RFx lists are multi-user: another buyer in the same company can create,
		// archive or award an RFx while this user sits on a tab. `staleTime` alone
		// would serve that user a cached list (and cached tab counts, which ride
		// the same response) for a full 5 minutes with no network call.
		//
		// "always" refetches on every mount — i.e. every tab switch — while React
		// Query keeps showing the cached rows, so the list still paints instantly
		// and updates in place when the response lands. `staleTime` is retained
		// deliberately: it still suppresses refetches from window focus and
		// remounts that are not user-initiated navigation.
		refetchOnMount: "always",
	});

	const tabPayload = query.data?.tabs?.[tab];

	// Switching to a tab that is not in cache changes the query key, so
	// `query.data` is undefined for the duration of that fetch — which would
	// blank EVERY tab's badge, not just the one being loaded. Counts are
	// scope-wide and identical across tabs, so the last set we saw is still
	// correct; hold it until the new response replaces it.
	const lastCounts = useRef<RfxListTabCounts<TTab>>({});

	if (query.data?.counts && Object.keys(query.data.counts).length > 0) {
		lastCounts.current = query.data.counts;
	}

	return {
		...query,
		rows: tabPayload?.data ?? [],
		meta: tabPayload?.meta,
		filterOptions: query.data?.filterOptions ?? {},
		counts: query.data?.counts ?? lastCounts.current,
	};
};
